/**
 * Push the step checkpoint after every apply and every undo, not only when the
 * user navigates.
 *
 * Why it matters: uploads used to happen on step navigation alone. A surgeon who
 * undid a tool and then closed the tab left the server holding the *un-undone*
 * socket, so the next device to open the order saw work the user had explicitly
 * removed. The same gap swallowed applies — anything done after the last
 * navigation existed only on that machine.
 *
 * Two things keep this from becoming a flood:
 *
 *   debounce   a slider nudged eight times produces one upload, not eight. The
 *              window is generous because the mesh export is the expensive part.
 *   fingerprint `uploadStepCheckpoint` already skips an upload whose geometry and
 *              metadata are unchanged, so a redundant push costs one hash.
 *
 * Fire-and-forget by design: a failed push is a warning, never an interruption to
 * the work in progress. Navigation still performs its own synchronous-ish upload,
 * so a debounce that has not fired yet is not a lost checkpoint.
 */
import useEditorStore from '../../../shared/stores/useEditorStore';
import useWorkflowStore from '../../../shared/stores/useWorkflowStore';
import { loadBkDesignOrderContext } from '../../../shared/lib/bkDesignOrderContext';
import { uploadStepCheckpoint } from '../../../shared/lib/stepCheckpointUpload';
import { flushBeforeMeshQueue } from './beforeMeshUploader';

const LOG_PREFIX = '[CheckpointAutoPush]';

/**
 * Long enough that a burst of applies collapses into one upload, short enough that
 * closing the tab shortly after a change still finds the work saved.
 */
const DEBOUNCE_MS = 2500;

/** @type {Map<string, ReturnType<typeof setTimeout>>} */
const timers = new Map();
/** Steps with an upload in flight — a later request re-arms instead of racing. */
const inFlight = new Set();
const rearm = new Set();

/**
 * Ask for `stepId` to be pushed. Coalesces.
 *
 * @param {string} stepId
 * @param {string} reason  for the log line — 'apply' | 'undo' | 'discard'
 */
export function scheduleStepCheckpointPush(stepId, reason = 'change') {
  if (!stepId) return;

  if (inFlight.has(stepId)) {
    rearm.add(stepId);
    return;
  }

  const existing = timers.get(stepId);
  if (existing) clearTimeout(existing);

  timers.set(
    stepId,
    setTimeout(() => {
      timers.delete(stepId);
      void push(stepId, reason);
    }, DEBOUNCE_MS),
  );
}

async function push(stepId, reason) {
  const ctx = loadBkDesignOrderContext();
  if (!ctx?.sales_order || !ctx?.customer) return;

  const mesh = useEditorStore.getState().sceneRefs?.shadedMesh;
  if (!mesh?.geometry?.attributes?.position?.count) return;

  inFlight.add(stepId);
  try {
    // Let the before-mesh queue settle first so the manifest we upload records
    // accurate `uploaded` flags rather than optimistic ones. Short timeout: an
    // in-flight blob is not worth delaying the checkpoint for.
    await flushBeforeMeshQueue(3000);

    const res = await uploadStepCheckpoint(stepId, mesh, ctx.sales_order, ctx.customer);
    if (res?.skipped) {
      if (res.reason !== 'unchanged') {
        console.log(`${LOG_PREFIX} ${stepId} skipped after ${reason}: ${res.reason}`);
      }
    } else {
      console.log(`${LOG_PREFIX} pushed ${stepId} after ${reason}`);
      // Refresh the files meta so the previous-step-checkpoint undo path and the
      // portal both see the new version.
      void useWorkflowStore
        .getState()
        .actions.fetchScanFiles(ctx.sales_order, ctx.customer)
        .catch(() => {});
    }
  } catch (e) {
    console.warn(`${LOG_PREFIX} ${stepId} push failed after ${reason}`, e);
  } finally {
    inFlight.delete(stepId);
    if (rearm.delete(stepId)) scheduleStepCheckpointPush(stepId, reason);
  }
}

/**
 * Push everything pending right now, skipping the debounce. Used before the app
 * unloads and before a deliberate navigation.
 *
 * @returns {Promise<void>}
 */
export async function flushStepCheckpointPushes() {
  const pending = [...timers.keys()];
  for (const stepId of pending) {
    clearTimeout(timers.get(stepId));
    timers.delete(stepId);
  }
  await Promise.all(pending.map((stepId) => push(stepId, 'flush')));
}

/** Drop a scheduled push — the step's work is being discarded anyway. */
export function cancelStepCheckpointPush(stepId) {
  const t = timers.get(stepId);
  if (t) {
    clearTimeout(t);
    timers.delete(stepId);
  }
  rearm.delete(stepId);
}

export function pendingCheckpointPushes() {
  return timers.size + inFlight.size;
}
